// ✅ src/pages/VehicleV2/DieselConsumptionReport.jsx
// FULL UPDATED FILE
//
// ✅ common localStorage / sessionStorage pattern
// ✅ useMemo + useCallback + useRef pattern
// ✅ pageLoading + dropdownLoading + listLoading
// ✅ prevents repeated initial API calls
// ✅ safer search / view all / print / export flow
// ✅ keeps passed vehicle query params support
// ✅ different print page per grid
//
// Print routes mapping:
// 1) Monthly Diesel Consumption Report (Grid1) -> /vehicle/VehicleWiseReportPrint
// 2) Monthlywise Diesel Issue Details (Grid2) -> /vehicle/MonthlyDieselReportPrint
// 3) Diesel Consumption (Hr/Km) (Grid3 + Grid4) -> /vehicle/DieselConsumptionReport_Print?type=hr|km
// 4) VehicleWise Diesel Issue Report (Grid5) -> /vehicle/VehicleWiseDieselIssueReportPrint

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaFileExport,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/dieselConsumption";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const fmt2 = (v) =>
  toNum(v, 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const exportToCsv = (rows, filename = "diesel-consumption.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const val = r?.[h] ?? "";
          const s = String(val).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const createDefaultFilters = (branchType, branchId) => ({
  vehtype: "O",
  branch: branchType === "HO" ? "Select" : toStr(branchId),
  vehRegNo: "",
  month: "Select",
  year: "Select",
  fromDate: "",
  toDate: "",
  reportType: "Select",
  vehicleMainType: "Select",
  vehicleName: "",
  vehicleCode: "",
});

export default function DieselConsumptionReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toNum(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toNum(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     dropdown states
  =========================== */
  const [branches, setBranches] = useState([{ label: "Select", value: "Select" }]);
  const [years, setYears] = useState([{ label: "Select", value: "Select" }]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([
    { label: "Select", value: "Select" },
  ]);

  /* ===========================
     passed query param context
  =========================== */
  const [logCtx, setLogCtx] = useState({
    logvehicleid: "",
    logvehiclecode: "",
    logvehicledetid: "",
    logvehiclebranchid: "",
    logvehicleregno: "",
  });

  /* ===========================
     autocomplete
  =========================== */
  const [vehRegSuggestions, setVehRegSuggestions] = useState([]);
  const [showVehRegSug, setShowVehRegSug] = useState(false);

  /* ===========================
     filters
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);

  /* ===========================
     grid states
  =========================== */
  const [gvListItems, setGvListItems] = useState([]);
  const [gvMonthly, setGvMonthly] = useState([]);
  const [gvDieselHr, setGvDieselHr] = useState([]);
  const [gvDieselKm, setGvDieselKm] = useState([]);
  const [gvVehicleWise, setGvVehicleWise] = useState([]);

  /* ===========================
     styles
  =========================== */
  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `rounded-2xl shadow-sm border p-4 ${
        isDark ? "bg-gray-900/30 border-gray-700" : "bg-white border-gray-200"
      }`,
    [isDark]
  );

  /* ===========================
     passed query params
  =========================== */
  const readPassedContext = useCallback(() => {
    const ctx = {
      logvehicleid: toStr(searchParams.get("logvehicleid")),
      logvehiclecode: toStr(searchParams.get("logvehiclecode")),
      logvehicledetid: toStr(searchParams.get("logvehicledetid")),
      logvehiclebranchid: toStr(searchParams.get("logvehiclebranchid")),
      logvehicleregno: toStr(searchParams.get("logvehicleregno")),
    };

    const anyPassed =
      ctx.logvehicleid ||
      ctx.logvehiclecode ||
      ctx.logvehicledetid ||
      ctx.logvehiclebranchid ||
      ctx.logvehicleregno;

    if (!anyPassed) return null;
    return ctx;
  }, [searchParams]);

  /* ===========================
     common param builder
  =========================== */
  const buildParams = useCallback(
    (f = filters, ctx = logCtx) => {
      const p = {
        vehtype: toStr(f.vehtype || "O"),
        branch: session.branchType === "HO" ? toStr(f.branch) : String(session.branchId),
        vehRegNo: toStr(f.vehRegNo),
        month: toStr(f.month),
        year: toStr(f.year),
        fromDate: toStr(f.fromDate),
        toDate: toStr(f.toDate),
        vehicleMainType: toStr(f.vehicleMainType),
        vehicleName: toStr(f.vehicleName),
        vehicleCode: toStr(f.vehicleCode),

        logvehicleid: toStr(ctx.logvehicleid),
        logvehiclecode: toStr(ctx.logvehiclecode),
        logvehicledetid: toStr(ctx.logvehicledetid),
        logvehiclebranchid: toStr(ctx.logvehiclebranchid),
        logvehicleregno: toStr(ctx.logvehicleregno),
      };

      Object.keys(p).forEach((k) => {
        if (p[k] === undefined || p[k] === null) p[k] = "";
      });

      return p;
    },
    [filters, logCtx, session.branchId, session.branchType]
  );

  /* ===========================
     init loaders
  =========================== */
  const loadInit = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchType: session.branchType,
          branchId: session.branchId,
          loginUserId: session.loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      setBranches(
        toList(data.branches).length
          ? data.branches
          : [{ label: "Select", value: "Select" }]
      );
      setYears(
        toList(data.years).length
          ? data.years
          : [{ label: "Select", value: "Select" }]
      );
      setVehicleMainTypes(
        toList(data.vehicleMainTypes).length
          ? data.vehicleMainTypes
          : [{ label: "Select", value: "Select" }]
      );
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Failed to load dropdowns");
      setBranches([{ label: "Select", value: "Select" }]);
      setYears([{ label: "Select", value: "Select" }]);
      setVehicleMainTypes([{ label: "Select", value: "Select" }]);
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchType, session.branchId, session.loginUserId, commonHeaders]);

  /* ===========================
     grid loaders
  =========================== */
  const clearAllGrids = useCallback(() => {
    setGvListItems([]);
    setGvMonthly([]);
    setGvDieselHr([]);
    setGvDieselKm([]);
    setGvVehicleWise([]);
  }, []);

  const loadAllGrids = useCallback(
    async (nextFilters = filters, nextCtx = logCtx) => {
      try {
        setListLoading(true);

        const params = buildParams(nextFilters, nextCtx);

        const [a, b, c, d, e] = await Promise.all([
          axios.get(`${API_BASE}/list`, { params, headers: commonHeaders }),
          axios.get(`${API_BASE}/monthlyIssue`, { params, headers: commonHeaders }),
          axios.get(`${API_BASE}/hrConsumption`, { params, headers: commonHeaders }),
          axios.get(`${API_BASE}/kmConsumption`, { params, headers: commonHeaders }),
          axios.get(`${API_BASE}/vehicleWiseIssue`, { params, headers: commonHeaders }),
        ]);

        const pack = [a.data, b.data, c.data, d.data, e.data];
        const bad = pack.find((x) => !x?.success);

        if (bad) {
          throw new Error(bad?.message || "Load failed");
        }

        setGvListItems(toList(a.data.rows));
        setGvMonthly(toList(b.data.rows));
        setGvDieselHr(toList(c.data.rows));
        setGvDieselKm(toList(d.data.rows));
        setGvVehicleWise(toList(e.data.rows));
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        clearAllGrids();
      } finally {
        setListLoading(false);
      }
    },
    [filters, logCtx, buildParams, commonHeaders, clearAllGrids]
  );

  /* ===========================
     autocomplete
  =========================== */
  const fetchVehRegSuggestions = useCallback(
    async (prefix, nextBranch, nextVehType) => {
      try {
        const p = toStr(prefix);
        if (!p) {
          setVehRegSuggestions([]);
          return;
        }

        const { data } = await axios.get(`${API_BASE}/vehRegAuto`, {
          params: {
            prefix: p,
            branchId:
              session.branchType === "HO"
                ? toStr(nextBranch || filters.branch)
                : String(session.branchId),
            vehtype: toStr(nextVehType || filters.vehtype || "O"),
            logVehicleId: "0",
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          setVehRegSuggestions([]);
          return;
        }

        setVehRegSuggestions(toList(data.rows));
      } catch {
        setVehRegSuggestions([]);
      }
    },
    [filters.branch, filters.vehtype, session.branchType, session.branchId, commonHeaders]
  );

  /* ===========================
     page init
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);

      const ctx = readPassedContext();
      const baseFilters = createDefaultFilters(session.branchType, session.branchId);

      if (ctx) {
        setLogCtx(ctx);

        const nextFilters = {
          ...baseFilters,
          branch: ctx.logvehiclebranchid || baseFilters.branch,
          vehRegNo: ctx.logvehicleregno || "",
          vehicleCode: ctx.logvehiclecode || "",
        };

        setFilters(nextFilters);
        clearAllGrids();

        await loadInit();
        await loadAllGrids(nextFilters, ctx);
      } else {
        setLogCtx({
          logvehicleid: "",
          logvehiclecode: "",
          logvehicledetid: "",
          logvehiclebranchid: "",
          logvehicleregno: "",
        });

        setFilters(baseFilters);
        clearAllGrids();

        await loadInit();
        await loadAllGrids(baseFilters, {
          logvehicleid: "",
          logvehiclecode: "",
          logvehicledetid: "",
          logvehiclebranchid: "",
          logvehicleregno: "",
        });
      }
    } finally {
      setPageLoading(false);
    }
  }, [
    readPassedContext,
    session.branchType,
    session.branchId,
    clearAllGrids,
    loadInit,
    loadAllGrids,
  ]);

  /* ===========================
     handlers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      setField(name, value);

      if (name === "vehRegNo") {
        setShowVehRegSug(true);
        fetchVehRegSuggestions(value, filters.branch, filters.vehtype);
      }

      if (name === "branch") {
        setVehRegSuggestions([]);
      }

      if (name === "vehtype" && filters.vehRegNo) {
        fetchVehRegSuggestions(filters.vehRegNo, filters.branch, value);
      }
    },
    [setField, fetchVehRegSuggestions, filters.branch, filters.vehtype, filters.vehRegNo]
  );

  const selectVehReg = useCallback((val) => {
    setFilters((prev) => ({ ...prev, vehRegNo: val }));
    setShowVehRegSug(false);
  }, []);

  const handleSearch = useCallback(async () => {
    if (isSelect(filters.month) || isSelect(filters.year)) {
      toast.error("Please select Month and Year");
      return;
    }

    await loadAllGrids(filters, logCtx);
    toast.success("Search completed");
  }, [filters, logCtx, loadAllGrids]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);

    const merged = {
      ...reset,
      branch:
        session.branchType === "HO"
          ? "Select"
          : String(session.branchId),
      vehicleCode: logCtx.logvehiclecode || "",
      vehRegNo: logCtx.logvehicleregno || "",
    };

    setFilters(merged);
    await loadAllGrids(merged, logCtx);
    toast.success("View all loaded");
  }, [session.branchType, session.branchId, logCtx, loadAllGrids]);

  const handleReset = useCallback(() => {
    const reset = createDefaultFilters(session.branchType, session.branchId);

    const merged = {
      ...reset,
      vehicleCode: logCtx.logvehiclecode || "",
      vehRegNo: logCtx.logvehicleregno || "",
      branch:
        logCtx.logvehiclebranchid ||
        (session.branchType === "HO" ? "Select" : String(session.branchId)),
    };

    setFilters(merged);
    clearAllGrids();
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchType, session.branchId, logCtx, clearAllGrids]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const openPrintPage = useCallback(
    (path, extra = {}) => {
      const q = new URLSearchParams({
        ...buildParams(filters, logCtx),
        ...extra,
      });
      window.open(`${path}?${q.toString()}`, "_blank", "noopener,noreferrer");
    },
    [buildParams, filters, logCtx]
  );

  const printMonthlyDieselConsumption = useCallback(() => {
    openPrintPage("/vehicle/VehicleWiseReportPrint");
  }, [openPrintPage]);

  const printMonthlyWiseDieselIssue = useCallback(() => {
    openPrintPage("/vehicle/MonthlyDieselReportPrint");
  }, [openPrintPage]);

  const printDieselConsumption = useCallback(
    (type) => {
      openPrintPage("/vehicle/DieselConsumptionReport_Print", { type });
    },
    [openPrintPage]
  );

  const printVehicleWiseDieselIssue = useCallback(() => {
    openPrintPage("/vehicle/VehicleWiseDieselIssueReportPrint");
  }, [openPrintPage]);

  const handleExportGrid1 = useCallback(() => {
    if (!gvListItems.length) {
      toast.info("No rows to export");
      return;
    }

    exportToCsv(gvListItems, "monthly-diesel-consumption.csv");
    toast.success("Exported Grid1");
  }, [gvListItems]);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    loadPage();
  }, [loadPage]);

  /* ===========================
     computed
  =========================== */
  const vehicleWiseTotals = useMemo(() => {
    const rows = gvVehicleWise || [];
    const sumWorkingHRS = rows.reduce((a, r) => a + toNum(r?.workingHRS), 0);
    const sumWorkingKM = rows.reduce((a, r) => a + toNum(r?.workingkm), 0);
    const sumDieselIssue = rows.reduce((a, r) => a + toNum(r?.diselissue), 0);

    const n = rows.length || 1;
    return {
      avgWorkingHRS: sumWorkingHRS / n,
      avgWorkingKM: sumWorkingKM / n,
      avgDieselIssue: sumDieselIssue / n,
    };
  }, [gvVehicleWise]);

  const hasPassedCtx =
    logCtx.logvehicleid ||
    logCtx.logvehiclecode ||
    logCtx.logvehicledetid ||
    logCtx.logvehiclebranchid ||
    logCtx.logvehicleregno;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DIESEL CONSUMPTION REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Diesel Issue & Consumption Details
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} {" • "} User ID: {session.loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  Grid1: {gvListItems.length} • Grid2: {gvMonthly.length} • Grid3: {gvDieselHr.length} •
                  Grid4: {gvDieselKm.length} • Grid5: {gvVehicleWise.length}
                </span>

                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isBusy
                      ? isDark
                        ? "bg-yellow-900 text-yellow-100"
                        : "bg-yellow-100 text-yellow-800"
                      : isDark
                      ? "bg-green-900 text-green-100"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {pageLoading
                    ? "Loading Page"
                    : dropdownLoading
                    ? "Loading Dropdowns"
                    : listLoading
                    ? "Loading List"
                    : "Ready"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${showFilters ? "bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${showFilters ? "bg-gray-50" : ""}`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExportGrid1}
                disabled={isBusy || gvListItems.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy || gvListItems.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                Export Grid1
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {(pageLoading || dropdownLoading) ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                Loading...
              </h3>
              <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Passed Vehicle Context */}
              {hasPassedCtx && (
                <div className={`${cardClass} mb-6`}>
                  <div className={`text-base font-semibold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                    Passed Vehicle Context
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Vehicle ID</label>
                      <input className={inputClass} value={logCtx.logvehicleid} readOnly />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Code</label>
                      <input className={inputClass} value={logCtx.logvehiclecode} readOnly />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Det ID</label>
                      <input className={inputClass} value={logCtx.logvehicledetid} readOnly />
                    </div>

                    <div>
                      <label className={labelClass}>Branch ID</label>
                      <input className={inputClass} value={logCtx.logvehiclebranchid} readOnly />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Reg No</label>
                      <input className={inputClass} value={logCtx.logvehicleregno} readOnly />
                    </div>
                  </div>
                </div>
              )}

              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <FaFilter /> FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>Vehicle Type</label>
                      <div className="flex gap-6">
                        <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                          <input
                            type="radio"
                            name="vehtype"
                            value="O"
                            checked={filters.vehtype === "O"}
                            onChange={handleChange}
                            disabled={isBusy}
                          />
                          Own Vehicle
                        </label>

                        <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                          <input
                            type="radio"
                            name="vehtype"
                            value="H"
                            checked={filters.vehtype === "H"}
                            onChange={handleChange}
                            disabled={isBusy}
                          />
                          Hire Vehicle
                        </label>
                      </div>
                    </div>

                    <div className={`flex items-center gap-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      <FaInfoCircle />
                      <span className="text-sm">
                        Month + Year are mandatory. Dates are optional.
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        className={inputClass}
                        name="branch"
                        value={filters.branch}
                        onChange={handleChange}
                        disabled={isBusy || session.branchType !== "HO"}
                      >
                        {branches.map((b, i) => (
                          <option key={`${b.value}-${i}`} value={b.value}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="relative">
                      <label className={labelClass}>Vehicle Registration Number</label>
                      <input
                        className={inputClass}
                        name="vehRegNo"
                        value={filters.vehRegNo}
                        onChange={handleChange}
                        onBlur={() => setTimeout(() => setShowVehRegSug(false), 150)}
                        onFocus={() => setShowVehRegSug(true)}
                        placeholder="Type reg no..."
                        disabled={isBusy}
                      />

                      {showVehRegSug && (vehRegSuggestions?.length || 0) > 0 && (
                        <div
                          className={`absolute z-20 mt-1 w-full rounded-lg border max-h-56 overflow-auto ${
                            isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
                          }`}
                        >
                          {vehRegSuggestions.map((s, idx) => {
                            const label = toStr(s?.label ?? s?.value ?? s);
                            return (
                              <button
                                key={idx}
                                type="button"
                                className={`w-full text-left px-3 py-2 text-sm ${
                                  isDark ? "text-gray-200 hover:bg-gray-800" : "text-gray-800 hover:bg-gray-100"
                                }`}
                                onClick={() => selectVehReg(label)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className={labelClass}>
                        Month <span className="text-red-500">*</span>
                      </label>
                      <select
                        className={inputClass}
                        name="month"
                        value={filters.month}
                        onChange={handleChange}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        <option value="1">January</option>
                        <option value="2">February</option>
                        <option value="3">March</option>
                        <option value="4">April</option>
                        <option value="5">May</option>
                        <option value="6">June</option>
                        <option value="7">July</option>
                        <option value="8">August</option>
                        <option value="9">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>
                        Year <span className="text-red-500">*</span>
                      </label>
                      <select
                        className={inputClass}
                        name="year"
                        value={filters.year}
                        onChange={handleChange}
                        disabled={isBusy}
                      >
                        {years.map((y, i) => (
                          <option key={`${y.value}-${i}`} value={y.value}>
                            {y.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>From Date (dd/MM/yyyy)</label>
                      <input
                        className={inputClass}
                        name="fromDate"
                        value={filters.fromDate}
                        onChange={handleChange}
                        placeholder="dd/MM/yyyy"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>To Date (dd/MM/yyyy)</label>
                      <input
                        className={inputClass}
                        name="toDate"
                        value={filters.toDate}
                        onChange={handleChange}
                        placeholder="dd/MM/yyyy"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Type</label>
                      <select
                        className={inputClass}
                        name="reportType"
                        value={filters.reportType}
                        onChange={handleChange}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        <option value="Vehiclewise Diesel Report">Vehiclewise Diesel Report</option>
                        <option value="Monthly Diesel Issue">Monthly Diesel Issue</option>
                        <option value="Diesel Consumption">Diesel Consumption</option>
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Main Type</label>
                      <select
                        className={inputClass}
                        name="vehicleMainType"
                        value={filters.vehicleMainType}
                        onChange={handleChange}
                        disabled={isBusy}
                      >
                        {vehicleMainTypes.map((t, i) => (
                          <option key={`${t.value}-${i}`} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Name</label>
                      <input
                        className={inputClass}
                        name="vehicleName"
                        value={filters.vehicleName}
                        onChange={handleChange}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Code</label>
                      <input
                        className={inputClass}
                        name="vehicleCode"
                        value={filters.vehicleCode}
                        onChange={handleChange}
                        disabled={isBusy}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {pageLoading
                        ? "Loading page..."
                        : dropdownLoading
                        ? "Loading dropdowns..."
                        : listLoading
                        ? "Loading list..."
                        : "Ready"}
                    </div>

                    <div className="flex gap-4 flex-wrap">
                      <button
                        onClick={handleReset}
                        type="button"
                        disabled={isBusy}
                        className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        Clear / Reset
                      </button>

                      <button
                        onClick={handleViewAll}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaEye />
                        View All
                      </button>

                      <button
                        onClick={handleSearch}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaSearch />
                        Search
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* GRID 1 */}
              <SectionHeader
                title="Monthly Diesel Consumption Report"
                onPrint={printMonthlyDieselConsumption}
                isBusy={isBusy}
                buttonClass={buttonClass}
                hoverClass={hoverClass}
                isDark={isDark}
              />
              <div className={`${cardClass} overflow-auto mb-6`}>
                <table className="min-w-full text-sm">
                  <thead className={isDark ? "text-gray-200" : "text-gray-700"}>
                    <tr className={isDark ? "bg-gray-800/40" : "bg-gray-100"}>
                      <th className="p-2 text-center">S.No</th>
                      <th className="p-2 text-center">Vehicle Main Type</th>
                      <th className="p-2 text-center">Vehicle Code</th>
                      <th className="p-2 text-center">Vehicle Reg No</th>
                      <th className="p-2 text-center">Vehicle Name</th>
                      <th className="p-2 text-center">Monthly Hr</th>
                      <th className="p-2 text-center">Monthly KM</th>
                      <th className="p-2 text-center">Monthly Diesel</th>
                      <th className="p-2 text-center">Diesel Cons/Hr</th>
                      <th className="p-2 text-center">Diesel Cons/KM</th>
                      <th className="p-2 text-center">Cumulative HR</th>
                      <th className="p-2 text-center">Cumulative KM</th>
                    </tr>
                  </thead>

                  <tbody className={isDark ? "text-gray-100" : "text-gray-800"}>
                    {gvListItems.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="p-4 text-center">
                          No Records Found!
                        </td>
                      </tr>
                    ) : (
                      gvListItems.map((r, idx) => (
                        <tr key={idx} className={isDark ? "border-b border-gray-700" : "border-b"}>
                          <td className="p-2 text-center">{idx + 1}</td>
                          <td className="p-2 text-center">{toStr(r?.VehicleMainType)}</td>
                          <td className="p-2 text-center">{toStr(r?.fld_vehicle_code)}</td>
                          <td className="p-2 text-center">{toStr(r?.fld_vehicle_regno)}</td>
                          <td className="p-2 text-center">{toStr(r?.fld_vehicle_name)}</td>
                          <td className="p-2 text-center">{toStr(r?.Whrs)}</td>
                          <td className="p-2 text-center">{toStr(r?.WKM)}</td>
                          <td className="p-2 text-center">{toStr(r?.disqty)}</td>
                          <td className="p-2 text-center">{toStr(r?.disconsumptionhr)}</td>
                          <td className="p-2 text-center">{toStr(r?.disconsumptionkm)}</td>
                          <td className="p-2 text-center">{toStr(r?.cumulativehrs)}</td>
                          <td className="p-2 text-center">{toStr(r?.cumulativekm)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* GRID 2 */}
              <SectionHeader
                title="Monthlywise Diesel Issue Details"
                onPrint={printMonthlyWiseDieselIssue}
                isBusy={isBusy}
                buttonClass={buttonClass}
                hoverClass={hoverClass}
                isDark={isDark}
              />
              <div className={`${cardClass} overflow-auto mb-6`}>
                <AutoGrid rows={gvMonthly} isDark={isDark} />
              </div>

              {/* GRID 3 */}
              <SectionHeader
                title="Diesel Consumption For Hr Basis Vehicle"
                onPrint={() => printDieselConsumption("hr")}
                isBusy={isBusy}
                buttonClass={buttonClass}
                hoverClass={hoverClass}
                isDark={isDark}
              />
              <div className={`${cardClass} overflow-auto mb-6`}>
                <AutoGrid rows={gvDieselHr} isDark={isDark} />
              </div>

              {/* GRID 4 */}
              <SectionHeader
                title="Diesel Consumption For KM Basis Vehicle"
                onPrint={() => printDieselConsumption("km")}
                isBusy={isBusy}
                buttonClass={buttonClass}
                hoverClass={hoverClass}
                isDark={isDark}
              />
              <div className={`${cardClass} overflow-auto mb-6`}>
                <AutoGrid rows={gvDieselKm} isDark={isDark} />
              </div>

              {/* GRID 5 */}
              <SectionHeader
                title="VehicleWise Diesel Issue Report"
                onPrint={printVehicleWiseDieselIssue}
                isBusy={isBusy}
                buttonClass={buttonClass}
                hoverClass={hoverClass}
                isDark={isDark}
              />
              <div className={`${cardClass} overflow-auto mb-6`}>
                <table className="min-w-full text-sm">
                  <thead className={isDark ? "text-gray-200" : "text-gray-700"}>
                    <tr className={isDark ? "bg-gray-800/40" : "bg-gray-100"}>
                      <th className="p-2 text-center">S.No</th>
                      <th className="p-2 text-center">Month Name</th>
                      <th className="p-2 text-center">Working HR</th>
                      <th className="p-2 text-center">Working KM</th>
                      <th className="p-2 text-center">Diesel Issue</th>
                      <th className="p-2 text-center">Diesel Cons HR</th>
                      <th className="p-2 text-center">Diesel Cons KM</th>
                      <th className="p-2 text-center">Cumulative HR</th>
                      <th className="p-2 text-center">Cumulative KM</th>
                    </tr>
                  </thead>

                  <tbody className={isDark ? "text-gray-100" : "text-gray-800"}>
                    {gvVehicleWise.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="p-4 text-center">
                          No Records Found!
                        </td>
                      </tr>
                    ) : (
                      gvVehicleWise.map((r, idx) => (
                        <tr key={idx} className={isDark ? "border-b border-gray-700" : "border-b"}>
                          <td className="p-2 text-center">{idx + 1}</td>
                          <td className="p-2 text-center">{toStr(r?.monthname)}</td>
                          <td className="p-2 text-center">{toStr(r?.workingHRS)}</td>
                          <td className="p-2 text-center">{toStr(r?.workingkm)}</td>
                          <td className="p-2 text-center">{toStr(r?.diselissue)}</td>
                          <td className="p-2 text-center">{toStr(r?.fld_diselconsumptionhr)}</td>
                          <td className="p-2 text-center">{toStr(r?.fld_diselconsumptionkm)}</td>
                          <td className="p-2 text-center">{toStr(r?.fld_cummulativehr)}</td>
                          <td className="p-2 text-center">{toStr(r?.fld_cummulativekm)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>

                  {gvVehicleWise.length > 0 && (
                    <tfoot>
                      <tr className={isDark ? "bg-gray-800/40 text-gray-100" : "bg-gray-100 text-gray-800"}>
                        <td className="p-2 text-center font-semibold" colSpan={2}>
                          Average Total
                        </td>
                        <td className="p-2 text-center font-semibold">{fmt2(vehicleWiseTotals.avgWorkingHRS)}</td>
                        <td className="p-2 text-center font-semibold">{fmt2(vehicleWiseTotals.avgWorkingKM)}</td>
                        <td className="p-2 text-center font-semibold">{fmt2(vehicleWiseTotals.avgDieselIssue)}</td>
                        <td className="p-2 text-center" colSpan={4}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}

function SectionHeader({
  title,
  onPrint,
  isBusy,
  buttonClass,
  hoverClass,
  isDark,
}) {
  return (
    <div className="flex items-center justify-between mb-2">
      <div className={`text-base font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
        {title}
      </div>

      <button
        onClick={onPrint}
        disabled={isBusy}
        className={`px-3 py-2 rounded-lg text-white ${buttonClass} ${hoverClass} flex items-center gap-2 ${
          isBusy ? "opacity-60 cursor-not-allowed" : ""
        }`}
        type="button"
      >
        <FaPrint /> Print
      </button>
    </div>
  );
}

/** AutoGrid like ASPX AutoGenerateColumns="true" */
function AutoGrid({ rows, isDark }) {
  const cols = useMemo(() => {
    if (!rows?.length) return [];
    return Object.keys(rows[0] || {});
  }, [rows]);

  if (!rows?.length) {
    return (
      <div className={`p-4 text-center ${isDark ? "text-gray-100" : "text-gray-800"}`}>
        No Records Found!
      </div>
    );
  }

  return (
    <table className="min-w-full text-sm">
      <thead className={isDark ? "text-gray-200" : "text-gray-700"}>
        <tr className={isDark ? "bg-gray-800/40" : "bg-gray-100"}>
          <th className="p-2 text-center">S.No</th>
          {cols.map((c) => (
            <th key={c} className="p-2 text-center">
              {c}
            </th>
          ))}
        </tr>
      </thead>

      <tbody className={isDark ? "text-gray-100" : "text-gray-800"}>
        {rows.map((r, idx) => (
          <tr key={idx} className={isDark ? "border-b border-gray-700" : "border-b"}>
            <td className="p-2 text-center">{idx + 1}</td>
            {cols.map((c) => (
              <td key={c} className="p-2 text-center">
                {String(r?.[c] ?? "")}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}